import { Content, Overlay, Portal, Root, Title, Trigger } from '@radix-ui/react-dialog';
import cn from 'classnames';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface SheetProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  /** Accessible name for the sheet; rendered visually hidden and focused on open. */
  title: string;
  children?: (closeSheet: () => void) => ReactNode;
  hasCloseButton?: boolean;
  className?: string;
  onOverlayClick?: () => void;
  isOverlayDismissible?: boolean;
  'data-testid'?: string;
}

function getFocusedElement(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

export default function Sheet({
  isOpen,
  onOpenChange,
  trigger,
  title,
  children,
  hasCloseButton: _hasCloseButton,
  className,
  onOverlayClick,
  isOverlayDismissible = true,
  'data-testid': dataTestId,
}: SheetProps) {
  const [open, setOpen] = useState(isOpen || false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    setOpen(!!isOpen);
  }, [isOpen]);

  // Layout effect so the opener is read before Radix moves focus into the content.
  useLayoutEffect(() => {
    if (open && !wasOpenRef.current) openerRef.current = getFocusedElement();
    wasOpenRef.current = open;
  }, [open]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const _onOpenChange = useCallback((isOpen: boolean) => {
    if (onOpenChange && isOpen === undefined) {
      return onOpenChange(isOpen);
    } else if (onOpenChange) {
      onOpenChange(isOpen);
      setOpen(isOpen);
    } else {
      setOpen(isOpen);
    }
  }, []);

  const closeDialog = () => {
    _onOpenChange(false);
  };

  const preventClose = (e: any) => {
    if (isOverlayDismissible === false) {
      e?.preventDefault?.();
    }

    onOverlayClick?.();
  };

  return (
    <Root open={open} onOpenChange={_onOpenChange}>
      {trigger && <Trigger asChild>{trigger}</Trigger>}
      <Portal>
        <Overlay className='data-[state=open]:animate-overlayShow" fixed inset-0 z-[90] bg-black/40' />
        <Content
          onInteractOutside={preventClose}
          onEscapeKeyDown={preventClose}
          className={cn('fixed right-0 top-0 z-[100] h-full w-5/6 bg-white p-4 shadow focus:outline-none', className)}
          aria-describedby={undefined}
          data-testid={dataTestId}
          onOpenAutoFocus={(event) => {
            if (!titleRef.current) return;
            event.preventDefault();
            titleRef.current.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(event) => {
            const opener = openerRef.current;
            if (!opener?.isConnected) return;
            event.preventDefault();
            opener.focus();
          }}
        >
          <Title ref={titleRef} tabIndex={-1} className="sr-only">
            {title}
          </Title>
          {children?.(closeDialog)}
        </Content>
      </Portal>
    </Root>
  );
}
